/**
 * Workspace API
 *
 * API functions for workspace management using persisted backend APIs.
 * 
 * NEW MODEL (1 property → 1 workspace):
 * - Each property belongs to exactly ONE workspace
 * - Auto-grouping fallback is gated behind MIGRATION_MODE for transitional support
 * - Once migration is complete, auto-grouping will be removed
 */

import { getUserWorkspaces } from "./api";
import { getWorkspaceProperties, getAvailableProperties } from "./workspace-team";
import type { Workspace, Connector, ConnectorType, ConnectorStatus, WorkspacePropertyInfo } from "@/lib/types/workspace";
import type { TrackingStatus } from "@/lib/types/workspace-team";

// Re-export types for convenience
export type { Workspace, Connector } from "@/lib/types/workspace";

// Feature flag: Enable auto-grouping fallback during migration
// Set to false once all properties have been migrated to explicit workspace assignments
const MIGRATION_MODE = false;

// Extended workspace type with auto-grouping flag (for migration period only)
export interface WorkspaceWithGrouping extends Workspace {
  isAutoGrouped?: boolean; // True if properties are from domain-based fallback (migration only)
}

/**
 * Backend workspace response shape from getUserWorkspaces API
 */
interface BackendWorkspace {
  workspace_id: string;
  owner_user_id: string;
  name: string;
  description?: string;
  created_at: string;
  archived: boolean;
  isOwner: boolean;
}

/**
 * Backend workspace response with attached properties and connectors
 */
interface BackendWorkspaceWithDetails extends BackendWorkspace {
  properties?: Array<{
    property_id: string;
    name: string;
    domain: string;
  }>;
  connectors?: Array<{
    connector_id: string;
    type: string;
    name: string;
    status: string;
    last_sync_at?: string;
    config?: Record<string, any>;
    metadata?: Record<string, any>;
  }>;
}

/**
 * Transform backend workspace to frontend Workspace type
 */
function transformWorkspace(backend: BackendWorkspaceWithDetails): WorkspaceWithGrouping {
  // Transform connectors from backend format
  const connectors: Connector[] = (backend.connectors || []).map((c) => ({
    connectorId: c.connector_id,
    workspaceId: backend.workspace_id,
    type: c.type as ConnectorType,
    name: c.name,
    status: c.status as ConnectorStatus,
    enabled: true, // Default to enabled
    lastSyncAt: c.last_sync_at,
    config: c.config,
    metadata: c.metadata,
  }));

  // Transform properties from backend format (1:1 model)
  // Handle both snake_case (property_id) and camelCase (propertyId) from backend
  const properties: WorkspacePropertyInfo[] = (backend.properties || []).filter((p) => !(p as any).archived).map((p) => ({
    propertyId: p.property_id ?? (p as any).propertyId,
    name: p.name,
    domain: p.domain,
    enabled: (p as any).enabled ?? true,
    trackingStatus: ((p as any).tracking_status ?? (p as any).trackingStatus ?? 'not_installed') as TrackingStatus,
    subscription: (p as any).subscription,
    lastActivatedAt: (p as any).lastActivatedAt,
  }));

  return {
    workspaceId: backend.workspace_id,
    name: backend.name,
    description: backend.description,
    createdAt: backend.created_at,
    updatedAt: backend.created_at,
    connectors,
    properties,
    isAutoGrouped: false,
    propertyCount: (backend as any).property_count !== undefined
      ? parseInt((backend as any).property_count, 10)
      : undefined,
    settings: (backend as any).settings ?? undefined,
  };
}

/**
 * Extract root domain from a full domain
 * e.g., "app.humblytics.com" -> "humblytics.com"
 */
function extractRootDomain(domain: string): string {
  const cleanDomain = domain.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  const parts = cleanDomain.split(".");
  const knownTLDs = ["com", "net", "org", "io", "app", "dev", "co", "uk", "au", "ca", "health", "ai"];

  if (parts.length >= 2) {
    const tld = parts[parts.length - 1];
    const secondLevel = parts[parts.length - 2];

    if (parts.length >= 3 && knownTLDs.includes(secondLevel)) {
      return `${parts[parts.length - 3]}.${secondLevel}.${tld}`;
    }
    return `${secondLevel}.${tld}`;
  }
  return cleanDomain;
}

/**
 * Check if a property domain matches a workspace name pattern
 * Uses fuzzy matching based on root domain extraction
 */
function doesPropertyMatchWorkspace(propertyDomain: string, workspaceName: string): boolean {
  const propertyRoot = extractRootDomain(propertyDomain).toLowerCase();
  const workspaceNameLower = workspaceName.toLowerCase();
  
  // Extract potential domain from workspace name
  // e.g., "Matchday Health" -> check if property contains "matchday" or "health"
  const workspaceWords = workspaceNameLower
    .replace(/workspace/gi, "")
    .replace(/[^a-z0-9\s]/g, "")
    .split(/\s+/)
    .filter(w => w.length > 2);
  
  // Check if property root domain contains any workspace name words
  for (const word of workspaceWords) {
    if (propertyRoot.includes(word)) {
      return true;
    }
  }
  
  // Also check if workspace name contains the property's root domain (without TLD)
  const domainName = propertyRoot.split(".")[0];
  if (domainName && workspaceNameLower.includes(domainName)) {
    return true;
  }
  
  return false;
}

/**
 * Get all workspaces for the current user
 * 
 * NEW MODEL (1 property → 1 workspace):
 * - Each property belongs to exactly ONE workspace
 * - Auto-grouping fallback is only used during MIGRATION_MODE
 * - Once migration is complete, auto-grouping should be disabled
 */
export const getAllWorkspaces = async (token: string): Promise<WorkspaceWithGrouping[]> => {
  try {
    // Fetch persisted workspaces and all properties in parallel.
    // getAvailableProperties returns current_workspace_id per property (needed for enrichment).
    const [workspacesResponse, availablePropsResponse] = await Promise.all([
      getUserWorkspaces(token),
      getAvailableProperties(token),
    ]);

    const backendWorkspaces: BackendWorkspaceWithDetails[] =
      workspacesResponse?.workspaces || workspacesResponse || [];
    const allProperties = availablePropsResponse?.properties || [];

    if (!Array.isArray(backendWorkspaces)) {
      console.error("Unexpected workspace response format:", workspacesResponse);
      return [];
    }

    // Filter out archived workspaces
    const activeWorkspaces = backendWorkspaces.filter((w) => !w.archived);

    // Transform each workspace
    const workspaces: WorkspaceWithGrouping[] = activeWorkspaces.map(transformWorkspace);

    // Enrich workspaces that have no properties using the fetched allProperties data.
    // The getUserWorkspaces endpoint may not return inline properties, but getAllProperties
    // returns each property with current_workspace_id, so we can derive the counts here.
    const enrichedFromProperties = workspaces.map((workspace) => {
      if (workspace.properties && workspace.properties.length > 0) {
        return workspace;
      }
      const seenIds = new Set<string>();
      const attached: WorkspacePropertyInfo[] = allProperties
        .filter((p: any) => !p.archived && p.current_workspace_id === workspace.workspaceId)
        .reduce((acc: WorkspacePropertyInfo[], p: any) => {
          const id = p.propertyId ?? p.property_id;
          if (!id || seenIds.has(id)) return acc;
          seenIds.add(id);
          acc.push({
            propertyId: id,
            name: p.name,
            domain: (p.dataSources?.[0]?.config as any)?.domain ?? p.domain ?? p.name,
            enabled: p.enabled ?? true,
            trackingStatus: (p.tracking_status ?? p.trackingStatus ?? 'not_installed') as TrackingStatus,
            isStaging: p.is_staging ?? p.isStaging ?? false,
            subscription: p.subscription,
            lastActivatedAt: p.lastActivatedAt,
          });
          return acc;
        }, []);
      if (attached.length > 0) {
        return { ...workspace, properties: attached };
      }
      return workspace;
    });

    // Only apply auto-grouping fallback during migration period
    if (!MIGRATION_MODE) {
      return enrichedFromProperties;
    }

    // MIGRATION MODE: For workspaces with 0 properties, apply domain-based fallback
    const enrichedWorkspaces = workspaces.map((workspace) => {
      if (workspace.properties && workspace.properties.length > 0) {
        // Workspace has explicit property assignments, use as-is
        return workspace;
      }

      // Find properties that match this workspace by domain pattern
      const matchingProperties = allProperties
        .filter((prop: any) => !prop.archived)
        .filter((prop: any) => {
          const domain = prop.domain || 
            (prop.dataSources?.[0]?.config as any)?.domain || 
            prop.name || "";
          return doesPropertyMatchWorkspace(domain, workspace.name);
        })
        .map((prop: any): WorkspacePropertyInfo => ({
          propertyId: prop.propertyId,
          name: prop.name || prop.domain || "Untitled",
          domain: (prop.dataSources?.[0]?.config as any)?.domain || prop.domain || prop.name,
          enabled: true, // Default enabled for auto-grouped
          trackingStatus: 'not_installed' as TrackingStatus, // Unknown for auto-grouped
          subscription: prop.subscription,
          lastActivatedAt: prop.lastActivatedAt,
          isStaging: prop.isStaging ?? false,
        }));

      if (matchingProperties.length > 0) {
        // Create a synthetic Humblytics connector for the auto-grouped properties
        const humblyticsConnector: Connector = {
          connectorId: `humblytics-auto-${workspace.workspaceId}`,
          workspaceId: workspace.workspaceId,
          type: "humblytics" as ConnectorType,
          name: "Humblytics Tracking",
          status: "connected" as ConnectorStatus,
          enabled: true,
          lastSyncAt: new Date().toISOString(),
          config: {},
          metadata: {
            propertyIds: matchingProperties.map((p: WorkspacePropertyInfo) => p.propertyId),
          },
        };

        return {
          ...workspace,
          properties: matchingProperties,
          connectors: workspace.connectors.length > 0 
            ? workspace.connectors 
            : [humblyticsConnector],
          isAutoGrouped: true,
        };
      }

      // No matching properties found
      return workspace;
    });

    return enrichedWorkspaces;
  } catch (error) {
    console.error("Error fetching workspaces:", error);
    return [];
  }
};

/**
 * Get a single workspace by ID with its attached properties
 * Uses persisted backend workspace API with hybrid fallback
 *
 * @param workspaceIdOrSlug - Can be either a plain workspace ID or a slug-ID format
 */
export const getWorkspace = async (
  workspaceIdOrSlug: string,
  token: string
): Promise<WorkspaceWithGrouping | null> => {
  if (!workspaceIdOrSlug || workspaceIdOrSlug === "new") {
    return null;
  }

  try {
    // Get all workspaces (already enriched with hybrid model)
    const workspaces = await getAllWorkspaces(token);

    // Try exact match first (for plain workspace IDs)
    let workspace = workspaces.find((w) => w.workspaceId === workspaceIdOrSlug);

    if (workspace) {
      return await enrichWorkspaceWithProperties(workspace, token);
    }

    // If not found, try matching by generating expected slug-ID for each workspace
    for (const w of workspaces) {
      const workspaceNameSlug = w.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 50);
      const expectedSlugId = `${workspaceNameSlug}-${w.workspaceId}`;

      if (workspaceIdOrSlug === expectedSlugId) {
        return await enrichWorkspaceWithProperties(w, token);
      }

      if (workspaceIdOrSlug.endsWith(`-${w.workspaceId}`)) {
        return await enrichWorkspaceWithProperties(w, token);
      }
    }

    // Last resort: try to match by checking if any part of the slug matches workspace IDs
    const slugParts = workspaceIdOrSlug.split("-");
    for (let i = slugParts.length - 1; i >= 0; i--) {
      const possibleId = slugParts.slice(i).join("-");
      workspace = workspaces.find((w) => w.workspaceId === possibleId);
      if (workspace) {
        return await enrichWorkspaceWithProperties(workspace, token);
      }
    }

    return null;
  } catch (error) {
    console.error("Error fetching workspace:", error);
    return null;
  }
};

/**
 * Enrich a workspace with its properties from the dedicated API
 * Only fetches if workspace doesn't already have properties
 */
async function enrichWorkspaceWithProperties(
  workspace: WorkspaceWithGrouping,
  token: string
): Promise<WorkspaceWithGrouping> {
  // If workspace already has properties, use those
  if (workspace.properties && workspace.properties.length > 0) {
    return workspace;
  }

  try {
    const propertiesResponse = await getWorkspaceProperties(workspace.workspaceId, token);
    
    const properties: WorkspacePropertyInfo[] = (propertiesResponse.properties || []).filter((p) => !(p as any).archived).map((p) => ({
      propertyId: p.property_id ?? (p as any).propertyId,
      name: p.name,
      domain: p.domain,
      enabled: p.enabled ?? true,
      trackingStatus: p.tracking_status ?? (p as any).trackingStatus ?? 'not_installed',
      isStaging: (p as any).is_staging ?? (p as any).isStaging ?? false,
      subscription: (p as any).subscription,
      lastActivatedAt: (p as any).lastActivatedAt,
    }));

    if (properties.length > 0) {
      return {
        ...workspace,
        properties,
        isAutoGrouped: false, // Explicitly assigned from API
      };
    }

    return workspace;
  } catch (error) {
    console.error("Error fetching workspace properties:", error);
    return workspace;
  }
}

// ============================================================================
// Workspace Mutation APIs
// ============================================================================

const PUBLIC_IS_PROD = import.meta.env.VITE_PUBLIC_IS_PROD;
const PUBLIC_IS_LOCAL = import.meta.env.VITE_PUBLIC_IS_LOCAL;

const API_URL =
  PUBLIC_IS_PROD === "TRUE"
    ? `https://app.humblytics.com/api/v1/`
    : PUBLIC_IS_LOCAL === "TRUE"
    ? `http://localhost:3000/api/v1/`
    : `https://humblytics.onrender.com/api/v1/`;

/**
 * Create a new workspace
 * Calls POST /workspaces
 */
export const createWorkspace = async (
  name: string,
  description: string | undefined,
  token: string
): Promise<Workspace> => {
  const endpoint = "workspaces";
  const payload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
    body: JSON.stringify({ name, description }),
  };

  try {
    const response = await fetch(API_URL + endpoint, payload);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to create workspace: ${response.status}`
      );
    }
    const data = await response.json();
    
    // Transform backend response to frontend Workspace type
    const workspace = data.workspace || data;
    return {
      workspaceId: workspace.workspace_id,
      name: workspace.name,
      description: workspace.description,
      createdAt: workspace.created_at,
      updatedAt: workspace.updated_at || workspace.created_at,
      connectors: [],
      properties: [],
    };
  } catch (error) {
    console.error("Error creating workspace:", error);
    throw error;
  }
};

/**
 * Update a workspace (rename, description, settings)
 * Calls PUT /workspaces/:workspaceId
 */
export const updateWorkspace = async (
  workspaceId: string,
  updates: { name?: string; description?: string; settings?: Record<string, unknown> },
  token: string
): Promise<Workspace> => {
  const endpoint = `workspaces/${workspaceId}`;
  const payload = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
    body: JSON.stringify(updates),
  };

  try {
    const response = await fetch(API_URL + endpoint, payload);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to update workspace: ${response.status}`
      );
    }
    const data = await response.json();
    
    // Transform backend response to frontend Workspace type
    const workspace = data.workspace || data;
    return {
      workspaceId: workspace.workspace_id,
      name: workspace.name,
      description: workspace.description,
      createdAt: workspace.created_at,
      updatedAt: workspace.updated_at || workspace.created_at,
      connectors: [],
      properties: [],
    };
  } catch (error) {
    console.error("Error updating workspace:", error);
    throw error;
  }
};

/**
 * Delete (archive) a workspace
 * Calls DELETE /workspaces/:workspaceId
 */
export const deleteWorkspace = async (
  workspaceId: string,
  token: string
): Promise<void> => {
  const endpoint = `workspaces/${workspaceId}`;
  const payload = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include" as RequestCredentials,
  };

  try {
    const response = await fetch(API_URL + endpoint, payload);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || errorData.error || `Failed to delete workspace: ${response.status}`
      );
    }
  } catch (error) {
    console.error("Error deleting workspace:", error);
    throw error;
  }
};
